/**
 * /start/payment — funnel step 4.
 *
 * Shows the order summary from the sessionStorage selection and a
 * "Complete order" action → POST /funnel/checkout. With Stripe configured the
 * response carries a redirectUrl to Stripe-hosted Checkout (promo codes are
 * entered there; the selection is kept so a cancel-return can retry). Without
 * Stripe (local dev) the dummy provider marks the order paid instantly and we
 * go straight to confirmation. A 422 PRECONDITION_FAILED means the agreement
 * was never accepted → bounce back to /start/agreement. No selection in
 * storage → bounce back to /start/products.
 */
import { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { FunnelLayout } from '@/components/funnel/FunnelLayout';
import { useAddons, useOfferings, FUNNEL_STATE_QUERY_KEY } from '@/hooks/useFunnel';
import { apiClient } from '@/services/api-client';
import {
  clearFunnelSelection,
  clearShippingAddress,
  formatPrice,
  readFunnelSelection,
  readShippingAddress,
} from '@/lib/funnel';
import type { FunnelCheckoutResult } from '@/types';

export function PaymentPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  // Stripe's cancel_url sends the user back here with ?checkout=cancelled.
  const checkoutCancelled = searchParams.get('checkout') === 'cancelled';

  // Read once on mount — cleared only after a successful checkout.
  const [selection] = useState(readFunnelSelection);
  const [shipping] = useState(readShippingAddress);

  const {
    data: offerings,
    isLoading: offeringsLoading,
    isError: offeringsError,
    refetch: refetchOfferings,
  } = useOfferings();
  const {
    data: addons,
    isLoading: addonsLoading,
    isError: addonsError,
    refetch: refetchAddons,
  } = useAddons(selection?.offeringId);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!selection) {
    return <Navigate to="/start/products" replace />;
  }

  const offering = offerings?.find((o) => o.id === selection.offeringId) ?? null;
  // Coming-soon add-ons are excluded like incompatible ones — a stale saved
  // selection must not show (or charge for) an unpurchasable item (86bar0jc1).
  const chosenAddons = (addons ?? []).filter(
    (a) => selection.addonIds.includes(a.id) && a.status !== 'coming_soon'
  );
  const isLoading = offeringsLoading || addonsLoading;
  // Either fetch failing means the displayed summary could be wrong —
  // checkout must be blocked until both succeed (review F1).
  const isFetchError = offeringsError || addonsError;

  // The selected plan disappeared from the catalog (deactivated mid-funnel),
  // or was flipped to coming-soon — same treatment: back to product selection
  // with a clean slate (the server would reject the checkout anyway).
  if (!isLoading && offerings && (!offering || offering.status === 'coming_soon')) {
    clearFunnelSelection();
    return <Navigate to="/start/products" replace />;
  }

  // A physical order (orb) must carry a shipping address — if it's missing
  // (e.g. deep-linked straight to payment), bounce back to the shipping step.
  // The server also enforces this at checkout (review backstop).
  const requiresShipping =
    !!offering?.includesOrb || chosenAddons.some((a) => a.slug === 'physical-orb');
  if (!isLoading && !isFetchError && offering && requiresShipping && !shipping) {
    return <Navigate to="/start/shipping" replace />;
  }

  const totalCents = offering
    ? offering.priceCents + chosenAddons.reduce((sum, a) => sum + a.priceCents, 0)
    : 0;

  const handleCompleteOrder = async () => {
    setError('');
    setIsSubmitting(true);

    // Submit exactly what is displayed in the order summary (review F1) —
    // never the raw sessionStorage ids, which may include stale/incompatible
    // add-ons that are not shown (and thus not part of the displayed total).
    const response = await apiClient.post<FunnelCheckoutResult>('/api/v1/funnel/checkout', {
      offeringId: selection.offeringId,
      addonIds: chosenAddons.map((a) => a.id),
      ...(shipping ? { shippingAddress: shipping } : {}),
    });

    if (!response.success) {
      setIsSubmitting(false);
      if (response.error?.code === 'PRECONDITION_FAILED') {
        // Agreement not accepted — send the user back to step 2.
        navigate('/start/agreement', { replace: true });
        return;
      }
      setError(response.error?.message || 'Failed to complete your order. Please try again.');
      return;
    }

    if (response.data?.redirectUrl) {
      // Stripe-hosted Checkout. Keep the sessionStorage selection (a
      // cancel-return needs it to re-render this page) and the submitting
      // state (the redirect is imminent; re-clicks must stay disabled).
      window.location.assign(response.data.redirectUrl);
      return;
    }

    // Paid immediately (dummy provider, or an already-completed session).
    setIsSubmitting(false);
    clearFunnelSelection();
    clearShippingAddress();
    await queryClient.invalidateQueries({ queryKey: FUNNEL_STATE_QUERY_KEY });
    navigate('/start/confirmation');
  };

  return (
    <FunnelLayout step={5} width="2xl">
      <div className="mb-6 text-center">
        <h1 className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          Payment
        </h1>
        <p className="mt-2 text-muted-foreground">Review your order and confirm.</p>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="space-y-3 pt-6">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-5 w-full" />
          </CardContent>
        </Card>
      ) : isFetchError || !offering ? (
        <div className="space-y-6">
          <div className="flex flex-col items-center gap-3 rounded-md border border-border py-10 text-center">
            <p className="text-sm text-destructive">
              We could not load your order details. Please try again.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                if (offeringsError) void refetchOfferings();
                if (addonsError) void refetchAddons();
              }}
            >
              Try Again
            </Button>
          </div>
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
            <Button variant="outline" size="lg" onClick={() => navigate('/start/products')}>
              Back to Product Selection
            </Button>
            {/* Never allow checkout while the displayed summary is unknown (F1). */}
            <Button size="lg" disabled>
              Complete order
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <Card>
            <CardContent className="pt-6">
              <h2 className="mb-4 text-base font-semibold text-foreground">Order summary</h2>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-foreground">
                    {offering.name}
                    <span className="ml-2 text-xs text-muted-foreground">{offering.planLabel}</span>
                  </span>
                  <span className="text-foreground">{formatPrice(offering.priceCents)}</span>
                </div>
                {chosenAddons.map((addon) => (
                  <div key={addon.id} className="flex items-center justify-between">
                    <span className="text-muted-foreground">{addon.name}</span>
                    <span className="text-muted-foreground">{formatPrice(addon.priceCents)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between border-t border-border pt-3 text-base font-semibold text-foreground">
                  <span>Total</span>
                  <span>{formatPrice(totalCents)}</span>
                </div>
                {offering.billingType === 'first_year_then_recurring' &&
                  offering.recurringPriceCents != null && (
                    <p className="text-xs text-muted-foreground">
                      First year, then {formatPrice(offering.recurringPriceCents)}/
                      {offering.recurringInterval ?? 'month'} thereafter.
                    </p>
                  )}
              </div>
            </CardContent>
          </Card>

          {checkoutCancelled && !error && (
            <div className="rounded-md border border-border bg-muted/50 p-4">
              <p className="text-sm text-foreground">
                Your payment wasn't completed — no charge was made. You can try again below.
              </p>
            </div>
          )}

          <div className="flex items-start gap-3 rounded-md border border-border bg-muted/50 p-4">
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              You'll be redirected to Stripe's secure checkout to complete your payment. Have a
              promo code? You can enter it on the payment page.
            </p>
          </div>

          {error && <p className="text-center text-sm text-destructive">{error}</p>}

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/start/shipping')}
              disabled={isSubmitting}
            >
              Back to Shipping
            </Button>
            <Button size="lg" onClick={handleCompleteOrder} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Complete order
            </Button>
          </div>
        </div>
      )}
    </FunnelLayout>
  );
}
